import { type FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { BudgetStatus } from "@kase/my-marathon-communication";
import { useSignal } from "@preact/signals";
import { getPdfThumbnails, useApp } from "~/core";
import { Button, Checkbox, IconButton, VisibilityCheckbox } from "~/components";
import useBudgetApproval from "~/routes/project/useBudgetApproval";
import Desktop from "./Desktop";
import { FIELD_TO_UPDATE_KEY } from "./inline-text";
import { FieldKey, PrintContentProps } from "./interfaces";
import Mobile from "./Mobile";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import EditIcon from "@material-design-icons/svg/outlined/edit.svg";
import cn from "classnames";

const SETTINGS_GROUPS = [
    {
        name: "Phases",
        items: [
            {
                key: "phases",
                name: "Phases",
            },
            {
                key: "phaseAmount",
                name: "Phase Amount",
            },
        ],
    },
    {
        name: "Lines",
        items: [
            {
                key: "lines",
                name: "Lines",
            },
            {
                key: "lineAmount",
                name: "Line Amount",
            },
            {
                key: "zeroAmountLines",
                name: "Zero Lines",
            },
            {
                key: "hours",
                name: "Hours",
            },
        ],
    },
    {
        name: "General",
        items: [
            {
                key: "vat",
                name: "VAT",
            },
            {
                key: "timeline",
                name: "Timeline as Appendix",
            },
        ],
    },
] as const;
const OTHER_CHECKS = {
    breakBefore: {
        key: "breakBefore",
        name: "Page break",
    },
    breakAfter: {
        key: "breakAfter",
        name: "Page break",
    },
} as const;

type CheckKey =
    | (typeof SETTINGS_GROUPS)[number]["items"][number]["key"]
    | (typeof OTHER_CHECKS)[keyof typeof OTHER_CHECKS]["key"];

const UNCHECKED_BY_DEFAULT = ["timeline"];

const DEFAULT_CHECK_STATE = [
    ...SETTINGS_GROUPS.flatMap((item) =>
        item.items
            .filter((i) => !UNCHECKED_BY_DEFAULT.includes(i.key))
            .map((item) => item.key),
    ),
];

const PrintContent: FC<PrintContentProps> = ({
    budget,
    project,
    onClose,
    mobile,
    isSettingsOpen: isSettingsOpenProp,
}) => {
    const { api, snack } = useApp();
    const { approve } = useBudgetApproval(budget.id);
    const document = api.approve.doc(approve?.id);
    const checkState = useSignal<CheckKey[]>(DEFAULT_CHECK_STATE);
    const internalSettingsOpen = useSignal(false);
    const isSettingsOpen = isSettingsOpenProp ?? internalSettingsOpen;
    const textBefore = useSignal(budget.info.peek()?.text_before ?? "");
    const textAfter = useSignal(budget.info.peek()?.text_after ?? "");
    const printVersion = useSignal(0);
    const pdfUrl = useSignal("");
    const thumbnailsSignal = useSignal<string[]>([]);
    const activeIndex = useSignal(0);

    const frozen = budget.status !== BudgetStatus.BudgetStatusNone;
    const thumbnails = frozen
        ? (document?.value.thumbnails ?? [])
        : thumbnailsSignal.value;
    const hasPrev = activeIndex.value > 0;
    const hasNext = activeIndex.value < thumbnails.length - 1;

    const getHandleToggle = (key: CheckKey) => () => {
        if (checkState.value.includes(key)) {
            checkState.value = checkState.value.filter((item) => item !== key);
        } else {
            checkState.value = [...checkState.value, key];
        }
    };

    const getParams = () =>
        checkState.value.reduce(
            (acc, key) => {
                acc[key] = true;
                return acc;
            },
            {} as Record<CheckKey, boolean>,
        );

    useEffect(() => {
        if (budget.status !== BudgetStatus.BudgetStatusNone) {
            return;
        }

        api.budget
            .print(budget._id, getParams())
            .then((res) => {
                pdfUrl.value = res.url;
                getPdfThumbnails(res.url).then((thumbs) => {
                    thumbnailsSignal.value = thumbs;
                });
            })
            .catch((err) => {
                console.error(err);
                const message =
                    err instanceof Error
                        ? err.message
                        : "Failed to generate PDF";
                snack.error("Printout failed", message);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budget, checkState.value.join(","), printVersion.value]);

    useEffect(() => {
        if (!document && approve) {
            api.approve.open(approve.id, "budget");
        }

        if (budget.status !== BudgetStatus.BudgetStatusNone) {
            document?.value.render();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budget.status, document?.value, Boolean(approve)]);

    const lastSaved = useRef({
        text_before: budget.info.peek()?.text_before ?? "",
        text_after: budget.info.peek()?.text_after ?? "",
    });

    const handleSaveText = async (
        key: "text_before" | "text_after",
        value: string,
    ) => {
        if (value === lastSaved.current[key]) {
            return;
        }

        try {
            await api.budget.update({ id: budget._id, [key]: value });
            lastSaved.current[key] = value;
            printVersion.value = printVersion.peek() + 1;
        } catch (err) {
            console.error(err);
            const message =
                err instanceof Error ? err.message : "Failed to save text";
            snack.error("Failed to save", message);
        }
    };

    const viewerRef = useRef<HTMLDivElement>(null);
    const editingField = useSignal<FieldKey | null>(null);
    const fieldSignals = { before: textBefore, after: textAfter };

    const closeEditor = () => {
        const field = editingField.peek();

        if (field) {
            handleSaveText(
                FIELD_TO_UPDATE_KEY[field],
                fieldSignals[field].peek(),
            );
        }

        editingField.value = null;
    };

    const handlePrev = () => {
        if (hasPrev) {
            activeIndex.value = activeIndex.peek() - 1;
        }
    };

    const handleNext = () => {
        if (hasNext) {
            activeIndex.value = activeIndex.peek() + 1;
        }
    };

    const hasNavigation = thumbnails.length > 1;
    const finalPdfUrl = frozen ? document?.value.url : pdfUrl.value;

    const editorOverlay = editingField.value && (
        <>
            <div
                className="absolute inset-0 z-30 rounded-2xl bg-black/10"
                onClick={closeEditor}
            />
            <div className="absolute inset-x-4 top-4 z-40 rounded-2xl border border-gray-300 bg-white p-4 shadow-xl">
                <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-bold text-sky-600">
                        {editingField.value === "before"
                            ? "Text before"
                            : "Text after"}
                    </h4>
                    <IconButton title="Close" onClick={closeEditor}>
                        <CloseIcon width="20" height="20" />
                    </IconButton>
                </div>
                <textarea
                    autofocus
                    className="h-[40vh] w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:ring focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-white"
                    placeholder="Type text here..."
                    value={fieldSignals[editingField.value].value}
                    onInput={({ currentTarget }) =>
                        (fieldSignals[editingField.peek() as FieldKey].value =
                            currentTarget.value)
                    }
                />
            </div>
        </>
    );

    const settingsContent = !frozen && (
        <>
            {SETTINGS_GROUPS.map((group) => (
                <section key={group.name} className="mb-4">
                    <h4 className="mb-1 font-bold text-sky-500">
                        {group.name}
                    </h4>
                    <ul>
                        {group.items.map((item) => (
                            <li key={item.key} className="mb-1">
                                <VisibilityCheckbox
                                    label={item.name}
                                    disabled={frozen}
                                    checked={checkState.value.includes(
                                        item.key,
                                    )}
                                    onClick={getHandleToggle(item.key)}
                                />
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
            <section className="mb-4">
                <h4 className="mb-1 font-bold text-sky-500">Text</h4>
                <div className="flex flex-col gap-y-2">
                    <Button
                        variant="secondary"
                        disabled={frozen}
                        className={cn(
                            "justify-between gap-x-2",
                            !textBefore.value && "opacity-80",
                        )}
                        onClick={() => (editingField.value = "before")}
                    >
                        <span>
                            {textBefore.value ? "Edit" : "Add"} text before
                        </span>
                        <EditIcon
                            width="18"
                            height="18"
                            className="fill-current"
                        />
                    </Button>
                    <Checkbox
                        label="Page break"
                        disabled={frozen}
                        checked={checkState.value.includes("breakAfter")}
                        onClick={getHandleToggle("breakAfter")}
                    />
                    <Button
                        variant="secondary"
                        disabled={frozen}
                        className={cn(
                            "justify-between gap-x-2",
                            !textAfter.value && "opacity-80",
                        )}
                        onClick={() => (editingField.value = "after")}
                    >
                        <span>
                            {textAfter.value ? "Edit" : "Add"} text after
                        </span>
                        <EditIcon
                            width="18"
                            height="18"
                            className="fill-current"
                        />
                    </Button>
                    <Checkbox
                        label="Page break"
                        disabled={frozen}
                        checked={checkState.value.includes("breakBefore")}
                        onClick={getHandleToggle("breakBefore")}
                    />
                </div>
            </section>
        </>
    );

    const viewProps = {
        budget,
        project,
        onClose,
        frozen,
        hasNavigation,
        activeIndex,
        thumbnails,
        hasPrev,
        hasNext,
        onPrev: handlePrev,
        onNext: handleNext,
        finalPdfUrl,
        editorOverlay,
        settingsContent,
        viewerRef,
        getParams,
        isSettingsOpen,
        editingField,
        fieldSignals,
        closeEditor,
    };

    return mobile ? <Mobile {...viewProps} /> : <Desktop {...viewProps} />;
};

export default PrintContent;
